/**
 * kop-delivery-estimate
 * ---------------------------------------------------------------------------
 * Shows the expected delivery date on the PDP, computed live in the store
 * timezone (Europe/Amsterdam) so cut-off times stay accurate regardless of the
 * visitor's own clock. Delivery schedule mirrors the carrier table shared by
 * the Dutchies logistics team:
 *
 *   NL  order before 23:00  -> next working day, Saturday counts as delivery day
 *   BE  order before 22:00  -> next working day, Saturday counts as delivery day
 *   DE  order before 10:00  -> ships same day, delivery 1 to 2 working days
 *                              after 10:00 -> +1 working day before that window
 *   UK  (GB)                -> 3 to 4 working days
 *   ROW everything else     -> 4 working days
 *
 * Weekends are skipped for dispatch (Mon to Fri) and Sundays are never a
 * delivery day. All knobs come from data attributes set by the Liquid block,
 * so the schedule can be changed from the theme editor without touching JS.
 */
(function () {
  'use strict';

  if (customElements.get('kop-delivery-estimate')) return;

  var STORE_TZ = 'Europe/Amsterdam';

  var I18N = {
    nl: { today: 'Vandaag in huis', tomorrow: 'Morgen bezorgd', tomorrowHome: 'Morgen in huis', onDate: 'In huis op', range: 'Bezorging', between: 'tussen', and: 'en' },
    en: { today: 'Arrives today', tomorrow: 'Arrives tomorrow', tomorrowHome: 'Arrives tomorrow', onDate: 'Arrives', range: 'Delivery', between: 'between', and: 'and' },
    de: { today: 'Lieferung heute', tomorrow: 'Lieferung morgen', tomorrowHome: 'Lieferung morgen', onDate: 'Lieferung am', range: 'Lieferung', between: 'zwischen', and: 'und' },
    fr: { today: "Livraison aujourd'hui", tomorrow: 'Livraison demain', tomorrowHome: 'Livraison demain', onDate: 'Livraison le', range: 'Livraison', between: 'entre', and: 'et' },
    es: { today: 'Entrega hoy', tomorrow: 'Entrega mañana', tomorrowHome: 'Entrega mañana', onDate: 'Entrega el', range: 'Entrega', between: 'entre', and: 'y' },
    it: { today: 'Consegna oggi', tomorrow: 'Consegna domani', tomorrowHome: 'Consegna domani', onDate: 'Consegna il', range: 'Consegna', between: 'tra', and: 'e' }
  };

  function strings(locale) {
    var short = (locale || 'nl').slice(0, 2).toLowerCase();
    return I18N[short] || I18N.en;
  }

  // -- date helpers (calendar-only, tz-naive Date objects) --------------------
  function isWorkingDay(d) { var x = d.getDay(); return x >= 1 && x <= 5; }
  function isDeliveryDay(d, sat) { var x = d.getDay(); return sat ? (x >= 1 && x <= 6) : (x >= 1 && x <= 5); }
  function addDays(d, n) { var r = new Date(d.getFullYear(), d.getMonth(), d.getDate()); r.setDate(r.getDate() + n); return r; }
  function nextWorkingDay(d) { var r = addDays(d, 1); while (!isWorkingDay(r)) r = addDays(r, 1); return r; }
  function countDeliveryDays(from, n, sat) {
    var r = new Date(from.getFullYear(), from.getMonth(), from.getDate()), c = 0;
    while (c < n) { r = addDays(r, 1); if (isDeliveryDay(r, sat)) c++; }
    return r;
  }

  // Current wall-clock moment in the store timezone, as a tz-naive Date.
  function nowInStoreTz() {
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: STORE_TZ, year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', hour12: false
      }).formatToParts(new Date());
      var map = {};
      parts.forEach(function (p) { if (p.type !== 'literal') map[p.type] = parseInt(p.value, 10); });
      var hour = map.hour === 24 ? 0 : map.hour;
      return new Date(map.year, map.month - 1, map.day, hour, map.minute);
    } catch (e) {
      return new Date();
    }
  }

  function parseCutoff(value) {
    if (!value) return null;
    var m = String(value).match(/(\d{1,2})\s*[:.]?\s*(\d{2})?/);
    if (!m) return null;
    return [parseInt(m[1], 10), m[2] ? parseInt(m[2], 10) : 0];
  }

  function estimate(config, now) {
    var today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    var pastCutoff = false;
    if (config.cutoff) {
      var h = config.cutoff[0], mm = config.cutoff[1];
      pastCutoff = now.getHours() > h || (now.getHours() === h && now.getMinutes() >= mm);
    }
    var dispatch = (isWorkingDay(today) && !pastCutoff) ? today : nextWorkingDay(today);
    return {
      earliest: countDeliveryDays(dispatch, config.min, config.sat),
      latest: countDeliveryDays(dispatch, config.max, config.sat)
    };
  }

  function sameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  function buildConfig(el) {
    var iso = (el.getAttribute('data-country') || '').toUpperCase();
    if (iso === 'UK') iso = 'GB';
    var saturday = el.getAttribute('data-saturday') === 'true';
    var table = {
      NL: { cutoff: parseCutoff(el.getAttribute('data-cutoff-nl')) || [23, 0], sat: saturday, min: 1, max: 1 },
      BE: { cutoff: parseCutoff(el.getAttribute('data-cutoff-be')) || [22, 0], sat: saturday, min: 1, max: 1 },
      DE: {
        cutoff: parseCutoff(el.getAttribute('data-cutoff-de')) || [10, 0], sat: false,
        min: parseInt(el.getAttribute('data-de-min'), 10) || 1,
        max: parseInt(el.getAttribute('data-de-max'), 10) || 2
      },
      GB: {
        cutoff: null, sat: false,
        min: parseInt(el.getAttribute('data-uk-min'), 10) || 3,
        max: parseInt(el.getAttribute('data-uk-max'), 10) || 4
      },
      ROW: { cutoff: null, sat: false, min: parseInt(el.getAttribute('data-row-days'), 10) || 4, max: parseInt(el.getAttribute('data-row-days'), 10) || 4 }
    };
    return table[iso] || table.ROW;
  }

  function render(el) {
    var locale = el.getAttribute('data-locale') || document.documentElement.lang || 'nl';
    var t = strings(locale);
    var labelOverride = el.getAttribute('data-label');
    var showHome = el.getAttribute('data-show-home') === 'true';
    var now = nowInStoreTz();
    var result = estimate(buildConfig(el), now);
    var tomorrow = addDays(now, 1);

    var valueEl = el.querySelector('.kop-delivery__value');
    if (!valueEl) return;

    // Build a natural lead phrase (bold) plus a supporting date (light).
    var lead, detail;
    if (sameDay(result.earliest, result.latest)) {
      var single = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' }).format(result.earliest);
      if (sameDay(result.earliest, now)) {
        lead = t.today; detail = single;
      } else if (sameDay(result.earliest, tomorrow)) {
        lead = showHome ? t.tomorrowHome : t.tomorrow; detail = single;
      } else {
        lead = t.onDate; detail = single;
      }
    } else {
      var shortFmt = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' });
      lead = t.range;
      detail = t.between + ' ' + shortFmt.format(result.earliest) + ' ' + t.and + ' ' + shortFmt.format(result.latest);
    }

    // A label set in the theme editor overrides the automatic lead phrase.
    if (labelOverride && labelOverride.length) { lead = labelOverride; }

    var labelEl = el.querySelector('.kop-delivery__label');
    if (labelEl) labelEl.textContent = lead;
    valueEl.textContent = detail;
    el.removeAttribute('hidden');
  }

  function safeRender(el) {
    try { render(el); } catch (e) { /* leave the server-rendered fallback in place */ }
  }

  class KopDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      safeRender(this);
      // Re-evaluate at midnight so an open tab does not show a stale date.
      var now = nowInStoreTz();
      var midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 1);
      var ms = midnight.getTime() - now.getTime();
      if (ms > 0 && ms < 86400000) {
        var self = this;
        this._timer = setTimeout(function () { safeRender(self); }, ms);
      }
    }
    disconnectedCallback() { if (this._timer) clearTimeout(this._timer); }
  }

  customElements.define('kop-delivery-estimate', KopDeliveryEstimate);
})();